import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import {
  appThemeModeLabels,
  applyAppTheme,
  initAppTheme,
  persistAppTheme,
  resolveAppThemeMode,
  resolveEffectiveTheme,
  type AppThemeMode,
} from "./appTheme";

type ThemeContextValue = {
  mode: AppThemeMode;
  effectiveTheme: "light" | "dark";
  setMode(mode: AppThemeMode): void;
};

const ThemeContext = createContext<ThemeContextValue | null>(null);

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [mode, setModeState] = useState<AppThemeMode>(() => {
    if (typeof window === "undefined") return "system";
    return resolveAppThemeMode(window.localStorage.getItem("fswl-app-theme"));
  });
  const [effectiveTheme, setEffectiveTheme] = useState<"light" | "dark">(() => resolveEffectiveTheme(mode));

  useEffect(() => {
    initAppTheme();
  }, []);

  useEffect(() => {
    setEffectiveTheme(resolveEffectiveTheme(mode));
  }, [mode]);

  useEffect(() => {
    if (mode !== "system") return;
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => {
      applyAppTheme("system");
      setEffectiveTheme(getSystemTheme());
    };
    media.addEventListener("change", onChange);
    return () => media.removeEventListener("change", onChange);
  }, [mode]);

  const setMode = useCallback((next: AppThemeMode) => {
    setModeState(next);
    persistAppTheme(next);
    setEffectiveTheme(resolveEffectiveTheme(next));
  }, []);

  const value = useMemo(
    () => ({ mode, effectiveTheme, setMode }),
    [effectiveTheme, mode, setMode],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

function getSystemTheme(): "light" | "dark" {
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

export function useAppTheme() {
  const context = useContext(ThemeContext);
  if (!context) throw new Error("useAppTheme must be used within ThemeProvider.");
  return context;
}

export { appThemeModeLabels };
